import type { ReactNode } from 'react'
import { fbt } from 'fbtee'
import { Calendar, CircleX, Compass, Heart } from 'lucide-react'
import { formatEventTimeRange, parseDateOnly, toBcp47 } from '@shared/dates'
import { formatEventDateRange } from '@/lib/formatDate'
import { cn } from '@/lib/utils'
import { GoingCheckIcon } from '@/components/GoingCheckIcon'
import { ResultButton, SearchThumb } from '@/components/GlobalSearch/SearchResultRows'
import type { EventCardEvent } from '@/components/EventCard'
import type { AgendaOwner } from './PlansAgenda'

/** One calendar day's plans, listed in the popover a tap on the day opens. */
export function PlansDayList<E extends EventCardEvent>({
  day,
  events,
  locale,
  ownerOf,
  extraOf,
  onOpen,
  onDiscover,
}: {
  day: string
  events: E[]
  locale: string
  ownerOf: (event: E) => DayOwner | undefined
  /** Appended to a row's meta line, e.g. its pre-game time. */
  extraOf?: (event: E) => ReactNode
  onOpen: (event: E) => void
  /** Browse Discover for this day; omitted for a past day, which Discover can't show. */
  onDiscover?: () => void
}) {
  const title = parseDateOnly(day).toLocaleDateString(toBcp47(locale), { weekday: 'long', month: 'long', day: 'numeric' })
  return (
    <>
      <div className="px-2 pt-1 text-xs font-medium capitalize text-muted-foreground">{title}</div>
      {events.map(e => {
        const extra = extraOf?.(e)
        return (
        <ResultButton key={e.id} onClick={() => onOpen(e)}>
          <SearchThumb src={e.imageUrl} icon={Calendar} />
          <div className="min-w-0 flex-1">
            <div className={cn('truncate font-medium', e.cancelled && 'line-through')}>{e.name}</div>
            <div className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
              {/* The day is the popover's title, so a one-day event shows only its hours; one spanning days keeps its dates. */}
              <span className="truncate">
                {[e.venueName ?? e.location, formatEventTimeRange(e.startDate, e.endDate, locale) ?? formatEventDateRange(e.startDate, e.endDate, locale)]
                  .filter(Boolean).join(' · ')}
              </span>
              {extra && <span aria-hidden>·</span>}
              {extra}
            </div>
          </div>
          <OwnerMark owner={ownerOf(e)} />
        </ResultButton>
        )
      })}
      {events.length === 0 && (
        <div className="px-2 pt-1.5 text-sm text-muted-foreground">
          {fbt('No plans for you or your crew yet.', 'Plans calendar day popover: neither the user nor their friends or groups have plans on the tapped day')}
        </div>
      )}
      {onDiscover && (
        <ResultButton onClick={onDiscover} className="font-medium text-primary">
          <Compass className="size-4 shrink-0" />
          {events.length > 0
            ? fbt('Discover more events for this day', 'Plans calendar day popover: link to Discover filtered to the tapped day, below the plans already listed')
            : fbt('Discover events for this day', 'Plans calendar day popover: link to Discover filtered to the tapped day')}
        </ResultButton>
      )}
    </>
  )
}

/** A crew event the viewer declined still lists, marked with their "not going". */
type DayOwner = AgendaOwner | 'not_going'

function OwnerMark({ owner }: { owner: DayOwner | undefined }) {
  if (owner === 'going') {
    return <GoingCheckIcon className="size-4 shrink-0" aria-label={fbt('Going', 'Plans calendar day popover: the user is going to this event')} />
  }
  if (owner === 'interested') {
    return <Heart className="size-4 shrink-0 fill-interested text-interested" aria-label={fbt('Interested', 'Plans calendar day popover: the user is interested in this event')} />
  }
  if (owner === 'not_going') {
    return <CircleX className="size-4 shrink-0 text-muted-foreground" aria-label={fbt('Not going', 'Plans calendar day popover: the user said they are not going to this event')} />
  }
  if (owner === 'friends') return <span aria-hidden className="h-1 w-2.5 shrink-0 rounded-full bg-friends" />
  return null
}
